import type { RootParentSymbol } from '../revisions';
import type { RevisionPageComputed } from '../types';
import type { RevisionComputations } from './types';

interface PageComputationsIndexes {
    /** Index of page to their origin computed page. */
    pagesOriginIndex: Map<string, RevisionPageComputed>;
    /** Index of reusable content to their origin computed page. */
    reusableContentsOriginIndex: Map<string, RevisionPageComputed>;
    /** Index of files to their origin computed page. */
    filesOriginIndex: Map<string, RevisionPageComputed>;
    /** Index of parent/index to an original computed page. */
    parentIndex: Map<string | typeof RootParentSymbol, Map<number, RevisionPageComputed>>;
}

const indexesCache = new WeakMap<RevisionComputations, PageComputationsIndexes>();

/**
 * Build indexes to easily lookup the relationships between pages and their computed origins.
 */
export function getPageComputationIndexes(
    computations: RevisionComputations
): PageComputationsIndexes {
    const cached = indexesCache.get(computations);
    if (cached) {
        return cached;
    }

    const result: PageComputationsIndexes = {
        pagesOriginIndex: new Map(),
        reusableContentsOriginIndex: new Map(),
        filesOriginIndex: new Map(),
        parentIndex: new Map(),
    };

    Object.values(computations.pages).forEach(
        ({ computedPage, result: revisionLayer, parentId, index }) => {
            if (revisionLayer) {
                Object.keys(revisionLayer.pages).forEach((pageId) => {
                    result.pagesOriginIndex.set(pageId, computedPage);
                });
                Object.keys(revisionLayer.reusableContents).forEach((reusableContentId) => {
                    result.reusableContentsOriginIndex.set(reusableContentId, computedPage);
                });
                Object.keys(revisionLayer.files).forEach((fileId) => {
                    result.filesOriginIndex.set(fileId, computedPage);
                });
            }

            result.parentIndex.set(
                parentId,
                (result.parentIndex.get(parentId) ?? new Map()).set(index, computedPage)
            );
        }
    );

    return result;
}
